"use client";

import React, { useState } from "react";
import { ComponentConfig } from "@puckeditor/core";
import { useRouter, useSearchParams } from "next/navigation";
import { useQuery } from "convex/react";
import { api } from "@/convex/_generated/api";
import { businessData } from "@/data/business";
import { submitLead } from "@/lib/lead-client";

export interface ContactFormProps {
  heading?: string;
  intro?: string;
  formHeading?: string;
  initialProductInterest?: string;
  submitButtonText?: string;
  successHeading?: string;
  successMessage?: string;
  redirectAfterSubmit?: boolean;
  redirectPath?: string;
}

export const ContactFormBlock: ComponentConfig<ContactFormProps> = {
  label: "Contact Form Section",
  fields: {
    heading: { type: "text", label: "Heading" },
    intro: { type: "textarea", label: "Intro Subheadline" },
    formHeading: { type: "text", label: "Form Heading" },
    initialProductInterest: {
      type: "select",
      label: "Default Product Interest",
      options: [
        { label: "Advanced Skin Care", value: "Skin Care" },
        { label: "Scalp & Hair Therapeutics", value: "Hair Care" },
        { label: "Body & Dermal Wellness", value: "Body Care" },
        { label: "Pediatric & Maternity Care", value: "Baby Care" },
        { label: "Men's Grooming", value: "Men Range" },
        { label: "Oral & Dental Hygiene", value: "Oral Care" },
        { label: "Pet Grooming", value: "Pet Care" },
        { label: "Suncare & Personal Hygiene", value: "Suncare" },
        { label: "Curated Bundles & Kits", value: "Kits" },
      ],
    },
    submitButtonText: { type: "text", label: "Submit Button Text" },
    successHeading: { type: "text", label: "Inline Success Heading" },
    successMessage: { type: "textarea", label: "Inline Success Message" },
    redirectAfterSubmit: {
      type: "radio",
      label: "Open Thank-You Page After Submit",
      options: [{ label: "Yes", value: true }, { label: "No", value: false }],
    },
    redirectPath: { type: "text", label: "Thank-You Page Path" },
  },
  defaultProps: {
    heading: "Formulate Your Next Product Line",
    intro: "Tell us about your intended product, target actives, packaging direction, market, and estimated first batch so our team can review the scope.",
    formHeading: "Send a Product Inquiry",
    initialProductInterest: "Skin Care",
    submitButtonText: "Submit Technical Brief",
    successHeading: "Inquiry received",
    successMessage: "Our formulation team will review your requirements and follow up using the contact details provided.",
    redirectAfterSubmit: true,
    redirectPath: "/thank-you",
  },
  render: (props) => <ContactFormSection {...props} />,
};

function ContactFormSection({
  heading,
  intro,
  formHeading,
  initialProductInterest = "",
  submitButtonText,
  successHeading,
  successMessage,
  redirectAfterSubmit = true,
  redirectPath = "/thank-you",
}: ContactFormProps) {
    const convexConfigured = Boolean(process.env.NEXT_PUBLIC_CONVEX_URL);
    const siteSettings = useQuery(api.siteSettings.getPublic, convexConfigured ? {} : "skip");
    const configuredPhone = siteSettings?.contactPhone || siteSettings?.phone || businessData.contact.phone;
    const contact = {
      ...businessData.contact,
      phone: configuredPhone,
      phoneRaw: siteSettings?.phoneRaw || String(configuredPhone).replace(/\D/g, ""),
      email: siteSettings?.contactEmail || businessData.contact.email,
      address: {
        ...businessData.contact.address,
        full: siteSettings?.address || businessData.contact.address.full,
      },
    };
    const router = useRouter();
    const searchParams = useSearchParams();
    const queryProduct = searchParams.get("product")?.trim().slice(0, 160) || "";
    const prefilledProduct = queryProduct || initialProductInterest;

    const [formState, setFormState] = useState({
      name: "",
      email: "",
      phone: "",
      company: "",
      productInterest: prefilledProduct || "Skin Care",
      batchSize: "500 Units",
      message: "",
      website: "",
    });

    const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
    const [errorMessage, setErrorMessage] = useState("");

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
      setFormState({ ...formState, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault();
      setStatus("submitting");
      setErrorMessage("");

      try {
        await submitLead({ ...formState, source: "contact_form" });

        setStatus("success");
        if (redirectAfterSubmit && redirectPath.startsWith("/")) {
          router.push(redirectPath);
        }
      } catch (err: unknown) {
        console.error("Submission error:", err);
        setStatus("error");
        setErrorMessage(err instanceof Error ? err.message : "Something went wrong. Please try calling or emailing us directly.");
      }
    };

    return (
      <div>
        <section className="page-hero">
          <div className="container">
            <h1>{heading}</h1>
            <p className="lead">{intro}</p>
          </div>
        </section>

        <section className="sec sec-alt">
          <div className="container contact-grid">
            <div className="contact-form-wrap">
              <h2>{formHeading || "Send a Product Inquiry"}</h2>

              {status === "success" ? (
                <div className="form-success" role="status" aria-live="polite">
                  <h3>{successHeading || "Inquiry received"}</h3>
                  <p>
                    Thank you, {formState.name}. {successMessage || "Our formulation team will review your requirements and follow up using the contact details provided."}
                  </p>
                </div>
              ) : (
                <form className="contact-form" onSubmit={handleSubmit}>
                  <label className="form-honeypot" aria-hidden="true">
                    Website
                    <input
                      type="text"
                      name="website"
                      value={formState.website}
                      onChange={handleChange}
                      tabIndex={-1}
                      autoComplete="off"
                    />
                  </label>
                  <div className="f-row">
                    <label>
                      Your Full Name *
                      <input
                        type="text"
                        name="name"
                        required
                        value={formState.name}
                        onChange={handleChange}
                        placeholder="e.g. Dr. Rajesh Sharma"
                      />
                    </label>
                    <label>
                      Work Email Address *
                      <input
                        type="email"
                        name="email"
                        required
                        value={formState.email}
                        onChange={handleChange}
                        placeholder="rajesh@brand.com"
                      />
                    </label>
                  </div>

                  <div className="f-row">
                    <label>
                      Phone / WhatsApp Number
                      <input
                        type="tel"
                        name="phone"
                        value={formState.phone}
                        onChange={handleChange}
                        placeholder="+91 98765 43210"
                      />
                    </label>
                    <label>
                      Company / Brand Name
                      <input
                        type="text"
                        name="company"
                        value={formState.company}
                        onChange={handleChange}
                        placeholder="Derma Wellness Pvt Ltd"
                      />
                    </label>
                  </div>

                  <div className="f-row">
                    <label>
                      Product or Suite
                      <select name="productInterest" value={formState.productInterest} onChange={handleChange}>
                        {prefilledProduct && !["Skin Care", "Hair Care", "Body Care", "Baby Care", "Men Range", "Oral Care", "Pet Care", "Suncare", "Kits"].includes(prefilledProduct) && (
                          <option value={prefilledProduct}>{prefilledProduct}</option>
                        )}
                        <option value="Skin Care">Advanced Skin Care</option>
                        <option value="Hair Care">Scalp & Hair Therapeutics</option>
                        <option value="Body Care">Body & Dermal Wellness</option>
                        <option value="Baby Care">Pediatric & Maternity Care</option>
                        <option value="Men Range">Men&apos;s Grooming</option>
                        <option value="Oral Care">Oral & Dental Hygiene</option>
                        <option value="Pet Care">Pet Grooming</option>
                        <option value="Suncare">Suncare & Hygiene</option>
                        <option value="Kits">Curated Bundles & Kits</option>
                      </select>
                    </label>
                    <label>
                      Estimated Initial Batch Size
                      <select name="batchSize" value={formState.batchSize} onChange={handleChange}>
                        <option value="500 Units">500 Units (Pilot Trial)</option>
                        <option value="2,500 Units">2,500 Units (Launch Batch)</option>
                        <option value="10,000 Units">10,000 Units (Scale-Up)</option>
                        <option value="50,000+ Units">50,000+ Units (Enterprise)</option>
                      </select>
                    </label>
                  </div>

                  <label>
                    Formulation Brief & Active Requirements *
                    <textarea
                      name="message"
                      rows={5}
                      required
                      value={formState.message}
                      onChange={handleChange}
                      placeholder="Describe target product type, key active ingredients (e.g. 10% Niacinamide + Salicylic Acid), preferred packaging, and target benchmark..."
                    ></textarea>
                  </label>

                  {status === "error" && <div className="form-error" role="alert">{errorMessage}</div>}

                  <button className="btn btn-primary" type="submit" disabled={status === "submitting"} aria-busy={status === "submitting"}>
                    {status === "submitting" ? "Sending Inquiry..." : (submitButtonText || "Submit Technical Brief")}{" "}
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M5 12h14M12 5l7 7-7 7" />
                    </svg>
                  </button>

                  <p className="form-note">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
                      <rect x="5" y="11" width="14" height="10" rx="2" />
                      <path d="M8 11V7a4 4 0 0 1 8 0v4" />
                    </svg>
                    Your formulation brief and product requirements are handled confidentially.
                  </p>
                </form>
              )}
            </div>

            <div className="contact-info">
              <h2>Direct Contact</h2>
              <ul className="ci-list">
                <li>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                  </svg>
                  <div>
                    <strong>Direct Sales Line</strong>
                    <a href={`tel:${contact.phoneRaw}`}>{contact.phone}</a>
                  </div>
                </li>
                <li>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                    <polyline points="22,6 12,13 2,6" />
                  </svg>
                  <div>
                    <strong>Inquiry Email</strong>
                    <a href={`mailto:${contact.email}`}>{contact.email}</a>
                  </div>
                </li>
                <li>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                    <circle cx="12" cy="10" r="3" />
                  </svg>
                  <div>
                    <strong>Manufacturing Plant</strong>
                    <span>{contact.address.full}</span>
                  </div>
                </li>
              </ul>

              <iframe
                className="map"
                src={`https://www.google.com/maps?q=${contact.geo.lat},${contact.geo.lng}&z=15&output=embed`}
                loading="lazy"
                title="Shaaira Biocare Location Map"
              ></iframe>
              <a className="map-link" href={contact.mapsUrl} target="_blank" rel="noopener">
                Open the manufacturing location in Google Maps
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </a>
            </div>
          </div>
        </section>
      </div>
    );
}
